// DocumentUploadView.tsx - Fixed status display logic
import React, { useState, useEffect } from 'react';
import { Upload, FileText, CheckCircle, X, AlertCircle, Loader, Eye, Download, Trash2, RefreshCw, File, Image, Info } from 'lucide-react';
import Toast from '../client/Toast';
import { uploadFileToPet } from '../../utils/directS3Upload';

interface DocumentType {
  id: string;
  name: string;
  description: string;
  required: boolean;
}

interface SelectedDocument {
  id: string;
  file: File;
  name: string;
  size: number;
  type: string;
  mimeType: string;
  status: 'ready' | 'selected' | 'uploaded' | 'pending' | 'uploading' | 'failed';
  docType: string;
  previewData: string | null;
  fileData: string | null;
  addedAt: Date;
  uploadedAt: string;
  petName: string;
  petIndex: number | null;
  petNumber: number;
  uploadStatus?: 'idle' | 'uploading' | 'success' | 'failed';
  restoredFromServer?: boolean;
}

interface DocumentForBackend {
  tempId: string;
  name: string;
  type: string;
  size: number;
  mimeType: string;
  fileData: string | null;
  file?: File | null;
  uploadStatus?: string;
  restoredFromServer?: boolean;
  uploadedAt: string;
  petName: string;
  petIndex: number | null;
  petNumber: number;
}

interface PetData {
  petName?: string;
  petIndex?: number;
  pet_id?: number;
}

interface QuestionnaireQuestion {
  id: string;
  question: string;
  type?: string;
  group?: string;
  show?: boolean;
  required?: boolean;
  order?: number;
}

interface QuestionnaireSettings {
  questionnaire?: QuestionnaireQuestion[];
}

interface DocumentUploadViewProps {
  onComplete: (documents: DocumentForBackend[]) => void;
  onPrevious: () => void;
  onClose?: () => void;
  isESA?: boolean;
  animalType?: 'pet' | 'esa' | 'sa';
  petData?: PetData;
  draftPetId?: number | string | null;
  ensureDraftPet?: () => Promise<number | null>;
  initialDocuments?: SelectedDocument[];
  onDocumentsChange?: (documents: SelectedDocument[]) => void;
  questionnaireSettings?: QuestionnaireSettings | null;
}

interface ToastState {
  isVisible: boolean;
  message: string;
  type: 'success' | 'error' | 'info' | 'warning';
}

interface FileStatus {
  status: string;
  text: string;
  color: string;
}

const DocumentUploadView: React.FC<DocumentUploadViewProps> = ({
  onComplete,
  onPrevious,
  onClose,
  isESA = false,
  animalType = 'pet',
  petData,
  draftPetId = null,
  ensureDraftPet,
  initialDocuments = [],
  onDocumentsChange,
  questionnaireSettings,
}) => {
  const [selectedDocs, setSelectedDocs] = useState<SelectedDocument[]>(initialDocuments);
  const [dragActive, setDragActive] = useState<boolean>(false);
  const [uploading, setUploading] = useState<boolean>(false);
  const [uploadProgress, setUploadProgress] = useState<Record<string, number>>({});
  const [previewDocument, setPreviewDocument] = useState<SelectedDocument | null>(null);
  const [showPreview, setShowPreview] = useState<boolean>(false);
  const [completingUpload, setCompletingUpload] = useState<boolean>(false);
  
  // Toast notification state
  const [toast, setToast] = useState<ToastState>({
    isVisible: false,
    message: '',
    type: 'success'
  });

  // Track if this is the first render
  const isFirstRender = React.useRef(true);
  
  // Store onDocumentsChange in a ref to avoid infinite loops
  const onDocumentsChangeRef = React.useRef(onDocumentsChange);
  
  useEffect(() => {
    onDocumentsChangeRef.current = onDocumentsChange;
  }, [onDocumentsChange]);

  // Sync with parent component's document state.
  // Use functional update to avoid overwriting local state with a stale empty
  // array when the async document-restore fetch hasn't completed yet.
  useEffect(() => {
    setSelectedDocs(prev => {
      if (prev === initialDocuments) return prev;
      if (prev.length === 0 && initialDocuments.length === 0) return prev;
      return initialDocuments;
    });
  }, [initialDocuments]);

  // Notify parent component whenever documents change (skip first render to avoid update-during-render warning)
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    
    if (onDocumentsChangeRef.current) {
      onDocumentsChangeRef.current(selectedDocs);
    }
  }, [selectedDocs]);

  // Update selected documents locally
  const updateSelectedDocs = (newDocsOrUpdater: SelectedDocument[] | ((prev: SelectedDocument[]) => SelectedDocument[])) => {
    setSelectedDocs(prev => {
      const newDocs = typeof newDocsOrUpdater === 'function' ? newDocsOrUpdater(prev) : newDocsOrUpdater;
      return newDocs;
    });
  };

  // Documents are data-driven from the admin questionnaire (group: 'documents', type: 'file').
  // ESA Letter stays hardcoded (option C) and is prepended when isESA — admin cannot toggle it.
  const dataDrivenDocs: DocumentType[] = (questionnaireSettings?.questionnaire || [])
    .filter(q =>
      q.group === 'documents' &&
      q.show !== false &&
      q.type === 'file'
    )
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
    .map(q => ({
      id: q.id,
      name: q.question,
      description: q.question,
      required: q.required === true,
    }));

  const requiredDocuments: DocumentType[] = isESA
    ? [
        { id: 'esa-letter', name: 'Emotional Support Animal Letter', description: 'Letter from licensed professional', required: true },
        ...dataDrivenDocs,
      ]
    : dataDrivenDocs;

  // Toast helper functions
  const showToast = (message: string, type: 'success' | 'error' | 'info' | 'warning' = 'success') => {
    setToast({
      isVisible: true,
      message,
      type
    });
  };

  const hideToast = () => {
    setToast(prev => ({ ...prev, isVisible: false }));
  };

  // Check for duplicate documents
  const isDuplicateDocument = (file: File, docType: string): boolean => {
    return selectedDocs.some(doc => 
      doc.docType !== docType && doc.name === file.name && doc.size === file.size
    );
  };

  const fileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = error => reject(error);
    });
  };

  const getFileIcon = (fileType: string, fileName: string) => {
    if (fileType.startsWith('image/')) {
      return <Image className="w-4 h-4" />;
    } else if (fileType === 'application/pdf') {
      return <FileText className="w-4 h-4" />;
    } else {
      return <File className="w-4 h-4" />;
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // FIXED: Correct status logic
  const getFileStatus = (doc: SelectedDocument): FileStatus => {
    if (doc.uploadStatus === 'uploading' || doc.status === 'uploading') {
      const progress = uploadProgress[doc.docType];
      return {
        status: 'uploading',
        text: progress !== undefined ? `Uploading... ${progress}%` : 'Uploading…',
        color: 'text-blue-600',
      };
    }
    if (doc.uploadStatus === 'failed' || doc.status === 'failed') {
      return {
        status: 'failed',
        text: 'Upload failed — retry',
        color: 'text-red-600',
      };
    }
    if (doc.uploadStatus === 'success' || doc.status === 'uploaded' || doc.restoredFromServer) {
      return {
        status: 'uploaded',
        text: 'Uploaded successfully',
        color: 'text-green-600',
      };
    }
    // If we're in the final completion stage, show uploading status
    if (completingUpload) {
      const progress = uploadProgress[doc.docType];
      if (progress !== undefined) {
        return { 
          status: 'uploading', 
          text: `Uploading... ${progress}%`, 
          color: 'text-blue-600' 
        };
      }
    }
    
    // For documents that are selected but not yet uploaded
    if (doc.status === 'ready' || doc.status === 'selected') {
      return { 
        status: 'ready', 
        text: 'Ready to upload', 
        color: 'text-green-600' 
      };
    }
    
    // Default status
    return { 
      status: 'pending', 
      text: 'Ready to upload', 
      color: 'text-gray-600' 
    };
  };

  const handleFiles = async (files: FileList, docType: string) => {
    const file = files[0];
    
    console.log('🔍 Processing file:', {
      name: file.name,
      type: file.type,
      size: file.size,
      docType: docType
    });
    
    // Check for duplicate documents in a different category
    if (isDuplicateDocument(file, docType)) {
      showToast('You cannot upload the same document to a different category. Please choose a different file or upload under the original category.', 'error');
      return;
    }
    
    // Validate file
    if (!validateFile(file)) {
      return;
    }

    // Set loading state for this specific document type
    setUploadProgress(prev => ({ ...prev, [docType]: 0 }));

    try {
      // Create a preview of the file for display
      let previewData: string | null = null;
      setUploadProgress(prev => ({ ...prev, [docType]: 20 }));
      
      try {
        // Create preview for images and PDFs under 5MB (matches validation limit)
        if (file.size < 5 * 1024 * 1024) {
          previewData = await fileToBase64(file);
        }
      } catch (error) {
        console.warn('Could not create file preview:', error);
      }
      
      setUploadProgress(prev => ({ ...prev, [docType]: 50 }));

      // Prefer small preview only — do not keep multi-MB base64 for submit
      let fileDataBase64: string | null = null;
      if (file.size < 500 * 1024 && file.type.startsWith('image/')) {
        try {
          fileDataBase64 = previewData;
        } catch {
          fileDataBase64 = null;
        }
      }

      setUploadProgress(prev => ({ ...prev, [docType]: 70 }));

      const docId = `temp_${Date.now()}_${Math.random()}`;
      let uploadStatus: SelectedDocument['uploadStatus'] = 'uploading';
      const newDoc: SelectedDocument = {
        id: docId,
        file: file,
        name: file.name,
        size: file.size,
        type: file.type,
        mimeType: file.type,
        status: 'uploading',
        docType: docType,
        previewData: previewData,
        fileData: fileDataBase64,
        addedAt: new Date(),
        uploadedAt: new Date().toISOString(),
        petName: petData?.petName || '',
        petIndex: petData?.petIndex !== undefined ? petData.petIndex : null,
        petNumber: petData?.petIndex !== undefined ? petData.petIndex + 1 : 1,
        uploadStatus,
      };

      updateSelectedDocs(prev => {
        const filtered = prev.filter(doc => doc.docType !== docType);
        return [...filtered, newDoc];
      });

      try {
        let petId = draftPetId ? Number(draftPetId) : null;
        if (!petId && ensureDraftPet) {
          petId = await ensureDraftPet();
        }
        if (petId) {
          const result = await uploadFileToPet({
            petId,
            file,
            uploadType: 'document',
            documentType: docType,
            documentName: file.name,
          });
          uploadStatus = result.success ? 'success' : 'failed';
        } else {
          uploadStatus = 'failed';
        }
      } catch (uploadErr) {
        console.error('Document upload failed:', uploadErr);
        uploadStatus = 'failed';
      }

      updateSelectedDocs(prev =>
        prev.map((doc) =>
          doc.id === docId
            ? {
                ...doc,
                status: uploadStatus === 'success' ? 'uploaded' : 'failed',
                uploadStatus,
                restoredFromServer: uploadStatus === 'success',
              }
            : doc,
        ),
      );

      setUploadProgress(prev => ({ ...prev, [docType]: 100 }));
      
      // Clear progress after a short delay
      setTimeout(() => {
        setUploadProgress(prev => {
          const newProgress = { ...prev };
          delete newProgress[docType];
          return newProgress;
        });
      }, 1000);

    } catch (error) {
      console.error('Error processing file:', error);
      alert('Error processing file. Please try again.');
      setUploadProgress(prev => {
        const newProgress = { ...prev };
        delete newProgress[docType];
        return newProgress;
      });
    }
  };

  const validateFile = (file: File): boolean => {
    const maxSize = 5 * 1024 * 1024; // 5MB
    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'];
    
    if (file.size > maxSize) {
      alert('File size must be less than 5MB');
      return false;
    }
    
    if (!allowedTypes.includes(file.type)) {
      alert('Only PDF, JPG, and PNG files are allowed');
      return false;
    }
    
    return true;
  };

  const handleFileUpload = async (docType: DocumentType) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.pdf,.jpg,.jpeg,.png';
    input.onchange = async (e: Event) => {
      const target = e.target as HTMLInputElement;
      const file = target.files?.[0];
      if (file && validateFile(file)) {
        await handleFiles(target.files!, docType.id);
      }
    };
    input.click();
  };

  const removeDocument = async (docId: string) => {
    updateSelectedDocs(prev => prev.filter(doc => doc.id !== docId));
  };

  const replaceDocument = async (docId: string, docType: string) => {
    removeDocument(docId);
    const docTypeObj = requiredDocuments.find(dt => dt.id === docType);
    if (docTypeObj) {
      handleFileUpload(docTypeObj);
    }
  };

  const handlePreviewDocument = (doc: SelectedDocument) => {
    if (doc.previewData && (doc.type.startsWith('image/') || doc.type === 'application/pdf')) {
      setPreviewDocument(doc);
      setShowPreview(true);
    } else {
      alert('Preview not available for this file type or file too large');
    }
  };

  const downloadDocument = (doc: SelectedDocument) => {
    if (doc.previewData) {
      const link = document.createElement('a');
      link.href = doc.previewData;
      link.download = doc.name;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } else {
      alert('Download not available - file too large for preview');
    }
  };

  const getSelectedDoc = (docTypeId: string): SelectedDocument | undefined => {
    return selectedDocs.find(doc => doc.docType === docTypeId);
  };

  const allRequiredSelected = (): boolean => {
    const requiredDocIds = requiredDocuments
      .filter(doc => doc.required)
      .map(doc => doc.id);
    
    return requiredDocIds.every((id) => {
      const doc = selectedDocs.find((d) => d.docType === id);
      if (!doc) return false;
      return (
        doc.uploadStatus === 'success' ||
        doc.status === 'uploaded' ||
        doc.restoredFromServer
      );
    });
  };

  const handleComplete = async () => {
    if (!allRequiredSelected()) {
      const failed = selectedDocs.some((d) => d.uploadStatus === 'failed');
      alert(
        failed
          ? 'One or more required documents failed to upload. Tap retry on the failed file.'
          : 'Please wait for required documents to finish uploading.',
      );
      return;
    }

    setCompletingUpload(true);
    setUploading(true);

    try {
      // Simulate final upload preparation progress
      for (const doc of selectedDocs) {
        setUploadProgress(prev => ({ ...prev, [doc.docType]: 0 }));
        await new Promise(resolve => setTimeout(resolve, 100));
        setUploadProgress(prev => ({ ...prev, [doc.docType]: 50 }));
        await new Promise(resolve => setTimeout(resolve, 100));
        setUploadProgress(prev => ({ ...prev, [doc.docType]: 100 }));
      }

      // Prepare documents for backend
      const documentsForBackend: DocumentForBackend[] = selectedDocs.map((doc, index) => {
        // Find the document type definition to get the display name
        const docTypeDef = requiredDocuments.find(d => d.id === doc.docType);
        const displayName = docTypeDef?.name || doc.docType;
        
        const fileExtension = doc.name.split('.').pop(); // Get original file extension
        // Generate name: "Document Name.ext" (e.g., "Vaccination Records.pdf")
        // No increment needed since we only keep the latest document per type
        const generatedName = `${displayName}.${fileExtension}`;
        
        console.log('🔍 Preparing document for backend:', {
          originalName: doc.name,
          generatedName: generatedName,
          displayName: displayName,
          type: doc.docType,
          mimeType: doc.mimeType,
          size: doc.size,
          hasFileData: !!doc.fileData,
          fileDataLength: doc.fileData?.length || 0,
          fileDataPreview: doc.fileData?.substring(0, 50) + '...'
        });
        
        return {
          tempId: doc.id,
          name: generatedName,
          type: doc.docType,
          size: doc.size,
          mimeType: doc.mimeType,
          fileData: null,
          file: doc.file || null,
          uploadStatus: doc.uploadStatus || (doc.restoredFromServer ? 'success' : 'idle'),
          restoredFromServer: doc.restoredFromServer,
          uploadedAt: doc.uploadedAt,
          petName: doc.petName,
          petIndex: doc.petIndex,
          petNumber: doc.petNumber
        };
      });

      console.log('Documents prepared for backend:', documentsForBackend.length);

      // Pass documents to parent component
      onComplete(documentsForBackend);

    } catch (error) {
      console.error('Document preparation failed:', error);
      alert('Failed to prepare documents. Please try again.');
    } finally {
      setCompletingUpload(false);
      setUploading(false);
      setUploadProgress({});
    }
  };

  const getPetDisplayName = (): string => {
    if (petData?.petName) {
      return petData.petName;
    }
    if (petData?.petIndex !== undefined) {
      return `Pet #${petData.petIndex + 1}`;
    }
    return 'your pet';
  };

  const getRegistrationType = (): string => {
    if (animalType === 'esa') {
      return 'Emotional Support Animal registration';
    } else if (animalType === 'sa') {
      return 'Service Animal registration';
    }
    return 'pet registration';
  };

  return (
    <div className="space-y-6">
      {/* Toast Notification */}
      <Toast
        message={toast.message}
        type={toast.type}
        isVisible={toast.isVisible}
        onClose={hideToast}
      />
      
      {/* Important Information Alert */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start space-x-3">
          <Info className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
          <div>
            <h4 className="font-medium text-blue-900 mb-2">Important Information</h4>
            <ul className="text-sm text-blue-800 space-y-1">
              <li>• All documents must be current and not expired</li>
              <li>• Insurance policy must cover liability for pet-related damages</li>
              <li>• Documents will be reviewed within 2-3 business days</li>
              <li>• Documents upload as soon as you select them</li>
              <li>• Make sure all documents are specifically for {getPetDisplayName()}</li>
            </ul>
          </div>
        </div>
      </div>

      {/* ESA Letter Requirements Warning */}
      {isESA && (
        <div className="bg-yellow-50 border border-yellow-300 rounded-lg p-4">
          <div className="flex items-start space-x-3">
            <AlertCircle className="w-5 h-5 text-yellow-600 mt-0.5 flex-shrink-0" />
            <div>
              <h4 className="font-medium text-yellow-900 mb-2">The ESA provider letter must meet the following criteria:</h4>
              <ul className="text-sm text-yellow-800 space-y-1">
                <li>• Letter explicitly names the resident as the patient</li>
                <li>• Letter is on professional letterhead</li>
                <li>• Health Professional Name is clearly identified</li>
                <li>• Health Professional Phone number is present</li>
                <li>• Letter is less than 12 months old (for housing purposes)</li>
                <li className=" text-red-900">
                <span className="font-bold text-red-900">Important: </span>
                  Online ESA certificates purchased from internet registries are NOT valid under the Fair Housing Act and will be flagged as insufficient documentation.</li>
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* Required Documents List */}
      <div className="space-y-4">
        {requiredDocuments.map(docType => {
          const selectedDoc = getSelectedDoc(docType.id);
          const fileStatus = selectedDoc ? getFileStatus(selectedDoc) : null;
          const isProcessing = uploadProgress[docType.id] !== undefined;
          
          return (
            <div key={docType.id}>
              {/* Document Label */}
              <div className="flex items-center justify-between mb-2">
                <label className="text-sm font-medium text-gray-900">
                  {docType.name} {docType.required && <span className="text-red-500">*</span>} 
                </label>
              </div>

              {/* Document Upload Area */}
              <div className={`border rounded-lg transition-colors ${
                selectedDoc 
                  ? selectedDoc.status === 'uploaded'
                    ? 'border-green-500 bg-green-50'
                    : 'border-green-500 bg-green-50' // FIXED: Show green when ready
                  : 'border-gray-300 bg-white hover:border-gray-400'
              }`}>
                
                {selectedDoc ? (
                  /* Selected Document Display */
                  <div className="p-4">
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center space-x-3 flex-1 min-w-0">
                        <div className="text-gray-600 flex-shrink-0">
                          {getFileIcon(selectedDoc.type, selectedDoc.name)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium text-gray-900 truncate">
                              {selectedDoc.name}
                            </span>
                            {/* FIXED: Show checkmark when ready or uploaded */}
                            {(selectedDoc.status === 'ready' || selectedDoc.status === 'uploaded' || selectedDoc.uploadStatus === 'success') && !isProcessing && (
                              <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                            )}
                            {selectedDoc.uploadStatus === 'failed' && (
                              <button
                                type="button"
                                className="text-xs text-red-600 underline flex-shrink-0"
                                onClick={() => {
                                  if (selectedDoc.file) {
                                    const dt = new DataTransfer();
                                    dt.items.add(selectedDoc.file);
                                    handleFiles(dt.files, selectedDoc.docType);
                                  }
                                }}
                              >
                                Retry
                              </button>
                            )}
                          </div>
                          <div className="flex flex-wrap items-center gap-x-4 gap-y-0.5 mt-1 text-xs text-gray-600">
                            <span>{formatFileSize(selectedDoc.size)}</span>
                            <span>Selected: {selectedDoc.addedAt.toLocaleDateString()}</span>
                            {/* Only surface in-progress / completed status — the green
                                checkmark already signals a file is selected, so we drop
                                the "Ready to upload" text that made residents re-upload. */}
                            {fileStatus && fileStatus.status !== 'ready' && fileStatus.status !== 'pending' && (
                              <span className={fileStatus.color}>
                                {fileStatus.text}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      
                      {/* Action Buttons */}
                      <div className="flex items-center gap-1 flex-shrink-0">
                        {/* Preview Button - Always show for images and PDFs */}
                        {selectedDoc && (selectedDoc.type?.startsWith('image/') || selectedDoc.type === 'application/pdf' || selectedDoc.mimeType?.startsWith('image/') || selectedDoc.mimeType === 'application/pdf') && (
                          <button
                            onClick={() => handlePreviewDocument(selectedDoc)}
                            disabled={completingUpload || isProcessing}
                            className="p-2 text-gray-500 hover:text-blue-600 rounded-lg hover:bg-gray-100 transition-colors disabled:opacity-50"
                            title="Preview document"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                        )}
                        
                        {/* Download Button */}
                        <button
                          onClick={() => downloadDocument(selectedDoc)}
                          disabled={completingUpload || isProcessing}
                          className="p-2 text-gray-500 hover:text-green-600 rounded-lg hover:bg-gray-100 transition-colors disabled:opacity-50"
                          title="Download document"
                        >
                          <Download className="w-4 h-4" />
                        </button>
                        
                        {/* Replace Button */}
                        <button
                          onClick={() => replaceDocument(selectedDoc.id, selectedDoc.docType)}
                          disabled={completingUpload || isProcessing}
                          className="p-2 text-gray-500 hover:text-orange-600 rounded-lg hover:bg-gray-100 transition-colors disabled:opacity-50"
                          title="Replace document"
                        >
                          <RefreshCw className="w-4 h-4" />
                        </button>
                        
                        {/* Remove Button */}
                        <button
                          onClick={() => removeDocument(selectedDoc.id)}
                          disabled={completingUpload || isProcessing}
                          className="p-2 text-gray-500 hover:text-red-600 rounded-lg hover:bg-gray-100 transition-colors disabled:opacity-50"
                          title="Remove document"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>

                    {/* Upload Progress - FIXED: Only show when actually processing */}
                    {isProcessing && (
                      <div className="mt-3">
                        <div className="flex items-center gap-2">
                          <div className="w-4 h-4 border-2 border-[color:var(--pmc-primary)] border-t-transparent rounded-full animate-spin" />
                          <span className="text-sm text-gray-600">
                            Processing... {uploadProgress[docType.id]}%
                          </span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
                          <div 
                            className="bg-[color:var(--pmc-primary)] h-2 rounded-full transition-all duration-300"
                            style={{ width: `${uploadProgress[docType.id] || 0}%` }}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  /* Upload Placeholder */
                  <div className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <Upload className="w-4 h-4 text-gray-400" />
                        <span className="text-sm text-gray-500">
                          {docType.required ? `${docType.description}` : `${docType.name} (optional)`}
                        </span>
                      </div>
                      <button
                        onClick={() => handleFileUpload(docType)}
                        disabled={completingUpload || isProcessing}
                        className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center"
                      >
                        {isProcessing ? (
                          <>
                            <span className="w-4 h-4 mr-2 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />
                            Processing...
                          </>
                        ) : (
                          <>
                            <Upload className="w-4 h-4 mr-2 inline" />
                            Select File
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Document Preview Modal */}
      {showPreview && previewDocument && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-900">
                Document Preview: {previewDocument.name}
              </h2>
              <button
                onClick={() => setShowPreview(false)}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>
            <div className="p-6 max-h-[calc(90vh-100px)] overflow-auto">
              {previewDocument.type.startsWith('image/') ? (
                <img
                  src={previewDocument.previewData || ''}
                  alt={previewDocument.name}
                  className="max-w-full h-auto mx-auto"
                />
              ) : previewDocument.type === 'application/pdf' ? (
                <iframe
                  src={previewDocument.previewData || ''}
                  className="w-full h-[600px] border-0"
                  title={previewDocument.name}
                />
              ) : (
                <p className="text-gray-600 text-center">
                  Preview not available for this file type
                </p>
              )}
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default DocumentUploadView;
